import { useQuery } from "@tanstack/react-query";

import { useAuth } from "../lib/auth";
import { colorOfUser } from "../lib/color";
import { formatDateTimeJa } from "../lib/dates";
import { formatBytes, formatDuration } from "../lib/media/format";
import { fetchMembers } from "../lib/media/members";
import { fetchMediaMeta, formatMime, formatResolution } from "../lib/media/meta";
import { fetchCoupleProfiles } from "../lib/profileItems";
import { queryKeys } from "../lib/queryKeys";
import QueryState from "./QueryState";
import SheetShell from "./SheetShell";

/**
 * 詳細表示から開くメタ情報の面。
 * スマホは下からのシート、PC は中央カード(SheetShell に任せる)。
 */
export default function MediaInfo({ mediaId, onClose }: { mediaId: string; onClose: () => void }) {
  const { session } = useAuth();
  const meta = useQuery({
    queryKey: queryKeys.mediaMeta(mediaId),
    queryFn: () => fetchMediaMeta(mediaId),
  });
  const members = useQuery({ queryKey: queryKeys.members, queryFn: fetchMembers });
  const profiles = useQuery({ queryKey: queryKeys.coupleProfiles, queryFn: fetchCoupleProfiles });

  /** 値の無い項目(寸法の無い古い行・写真の再生時間)は行ごと出さない */
  const rows: { label: string; value: string; color?: string }[] = [];
  if (meta.data !== undefined) {
    const item = meta.data;
    rows.push({ label: "種別", value: item.type === "video" ? "動画" : "写真" });
    rows.push({ label: "撮影日時", value: formatDateTimeJa(item.taken_at) });
    rows.push({ label: "追加日時", value: formatDateTimeJa(item.uploaded_at) });
    rows.push({
      label: "追加した人",
      value: members.data?.get(item.uploader_id) ?? "不明",
      color: colorOfUser(profiles.data, item.uploader_id, session?.user.id),
    });
    const resolution = formatResolution(item.width, item.height);
    if (resolution !== null) rows.push({ label: "解像度", value: resolution });
    rows.push({ label: "ファイルサイズ", value: formatBytes(item.byte_size) });
    rows.push({ label: "形式", value: formatMime(item.mime_type) });
    if (item.duration_seconds !== null) {
      rows.push({ label: "再生時間", value: formatDuration(item.duration_seconds) });
    }
  }

  return (
    <SheetShell title="情報" onClose={onClose} center>
      <div className="flex min-h-32 flex-col overflow-y-auto px-4 py-2">
        <QueryState
          isPending={meta.isPending}
          isError={meta.isError}
          errorMessage="情報の取得に失敗しました。"
          className="py-2 text-sm"
        />
        {meta.isSuccess && (
          <dl className="flex flex-col">
            {rows.map((row) => (
              <div
                key={row.label}
                className="flex items-baseline justify-between gap-4 border-b border-[var(--color-line)] py-2.5 last:border-b-0"
              >
                <dt className="shrink-0 text-xs text-[var(--color-sub)]">{row.label}</dt>
                <dd
                  className="text-right text-sm tabular-nums"
                  style={row.color === undefined ? undefined : { color: row.color }}
                >
                  {row.value}
                </dd>
              </div>
            ))}
          </dl>
        )}
      </div>
    </SheetShell>
  );
}
